import React from 'react';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Product, archiveProduct } from '@/data/productData';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { Archive, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ProductTableRowProps {
  product: Product;
  onArchive?: () => void;
}

const ProductTableRow: React.FC<ProductTableRowProps> = ({ 
  product, 
  onArchive 
}) => {
  const { toast } = useToast();
  
  const handleArchiveToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product) return;
    
    try {
      const newStatus = product.status === 'active' ? 'archived' : 'active';
      await archiveProduct(product.id, newStatus);
      
      if (onArchive) onArchive();
      
      toast({
        title: newStatus === 'archived' ? 'Product archived' : 'Product activated',
        description: `The product has been ${newStatus === 'archived' ? 'archived' : 'activated'} successfully.`
      });
    } catch (err) {
      console.error('Failed to toggle product status:', err);
      toast({
        title: "Operation failed",
        description: "Failed to update product status.",
        variant: "destructive"
      });
    }
  };
  
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'archived': return 'secondary';
      case 'draft': return 'outline';
      default: return 'secondary';
    }
  };
  
  const getStatusClassName = (status: string) => {
    switch (status) {
      case 'active': return 'border-green-300 bg-green-50 text-green-500 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/20 dark:text-green-400';
      case 'archived': return 'bg-gray-50 text-gray-800 ';
      case 'draft': return 'bg-yellow-200 hover:bg-yellow-300 text-yellow-800';
      default: return 'bg-gray-200 text-gray-800';
    }
  };

  return (
    <DataTable.Row 
      entityType="product" 
      entityId={product.id}
    >
      <DataTable.Cell className="font-medium">
        {product.name}
      </DataTable.Cell>
      <DataTable.Cell>
        {formatCurrency(product.price_cents || 0)}
      </DataTable.Cell>
      <DataTable.Cell>
        {product.billing_period === 'one_time' ? 
          'One-time' : 
          product.billing_period.charAt(0).toUpperCase() + product.billing_period.slice(1)}
      </DataTable.Cell>
      <DataTable.Cell>
        {formatDate(product.created_at)}
      </DataTable.Cell>
      <DataTable.Cell>
        <Badge 
          variant={getStatusVariant(product.status)} 
          className={`capitalize ${getStatusClassName(product.status)}  px-2.5 py-0.5 text-xs font-semibold`}
        >
          {product.status}
        </Badge>
      </DataTable.Cell>

      <DataTable.Actions className="flex justify-end items-center">
        <button
          title={product.status === 'archived' ? "Unarchive product" : "Archive product"}
          className="p-1 rounded hover:bg-muted group transition"
          onClick={handleArchiveToggle}
        >
          {product.status === 'archived' ? (
            <RotateCcw className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          ) : (
            <Archive className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
          )}
        </button>
      </DataTable.Actions>
    </DataTable.Row>
  );
};

export default ProductTableRow;